import React, { createContext, useContext, useState, useCallback } from 'react';

type DialogVariant = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
}

export interface AlertOptions {
  title?: string;
  message: string;
  okText?: string;
  variant?: DialogVariant;
}

export interface PromptOptions {
  title?: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
}

interface DialogState {
  isOpen: boolean;
  type: 'confirm' | 'alert' | 'help' | 'prompt';
  title?: string;
  message?: string;
  content?: string | React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  okText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  maxWidth?: string;
  maxHeight?: string;
  placeholder?: string;
  defaultValue?: string;
  onSubmit?: (value: string) => void;
}

interface DialogContextType {
  showConfirm: (options: {
    title?: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => void;
  showAlert: (options: {
    title?: string;
    message: string;
    onClose?: () => void;
    okText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => void;
  showHelp: (options: {
    title?: string;
    content: string | React.ReactNode;
    onClose?: () => void;
    maxWidth?: string;
    maxHeight?: string;
  }) => void;
  /** Resolves true when confirmed, false when cancelled or dismissed. */
  confirmAsync: (options: ConfirmOptions) => Promise<boolean>;
  /** Resolves when the alert is dismissed. */
  alertAsync: (options: AlertOptions) => Promise<void>;
  /** Resolves with the entered text, or null when cancelled. */
  promptAsync: (options: PromptOptions) => Promise<string | null>;
  closeDialog: () => void;
  dialogState: DialogState;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

interface DialogProviderProps {
  children: React.ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    type: 'confirm',
    message: '',
  });

  const closeDialog = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showConfirm = useCallback((options: {
    title?: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => {
    setDialogState({
      isOpen: true,
      type: 'confirm',
      title: options.title,
      message: options.message,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
      confirmText: options.confirmText,
      cancelText: options.cancelText,
      variant: options.variant || 'info',
    });
  }, []);

  const showAlert = useCallback((options: {
    title?: string;
    message: string;
    onClose?: () => void;
    okText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
  }) => {
    setDialogState({
      isOpen: true,
      type: 'alert',
      title: options.title,
      message: options.message,
      onCancel: options.onClose,
      okText: options.okText,
      variant: options.variant || 'info',
    });
  }, []);

  const showHelp = useCallback((options: {
    title?: string;
    content: string | React.ReactNode;
    onClose?: () => void;
    maxWidth?: string;
    maxHeight?: string;
  }) => {
    setDialogState({
      isOpen: true,
      type: 'help',
      title: options.title,
      content: options.content,
      onCancel: options.onClose,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
    });
  }, []);

  const confirmAsync = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setDialogState({
          isOpen: true,
          type: 'confirm',
          ...options,
          variant: options.variant || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    []
  );

  const alertAsync = useCallback(
    (options: AlertOptions) =>
      new Promise<void>((resolve) => {
        setDialogState({
          isOpen: true,
          type: 'alert',
          ...options,
          variant: options.variant || 'info',
          onCancel: () => resolve(),
        });
      }),
    []
  );

  const promptAsync = useCallback(
    (options: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setDialogState({
          isOpen: true,
          type: 'prompt',
          ...options,
          variant: options.variant || 'info',
          onSubmit: (value) => resolve(value),
          onCancel: () => resolve(null),
        });
      }),
    []
  );

  const value: DialogContextType = {
    showConfirm,
    showAlert,
    showHelp,
    confirmAsync,
    alertAsync,
    promptAsync,
    closeDialog,
    dialogState,
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
    </DialogContext.Provider>
  );
}; 